import { useEffect, useState } from 'react';
import {
  Coins, Save, RefreshCw, Loader2, Users, Plus, X,
} from 'lucide-react';
import {
  fetchRewardPointsSettings,
  updateRewardPointsSettings,
  invalidateRewardPointsCache,
  type RewardPointsSettings,
} from '@/lib/api';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './EarningRulesPage.module.css';

/**
 * Earning Rules — tenant-scoped tunables for the gamification
 * engine. Drives every earning pathway (kWh, session bonuses,
 * referrals, daily streaks, redemption thresholds, tier
 * multipliers).
 *
 * Lifted out of the prior Settings page (commit relocated this
 * domain-specific config off the catch-all Settings surface and
 * onto its own page under the GAMIFICATION nav section). Form
 * logic, save handler, force-refresh-cache button, NumericField
 * helper — all preserved 1:1 from the original Settings page so
 * existing operator muscle memory transfers unchanged.
 *
 * Saving any field invalidates the API's per-tenant cache so the
 * next earn picks up the new value immediately. The 60s server-
 * side TTL is the fallback safety net.
 */

// Tier list mirrors the Rank.tier enum in @evi/db. Renders one input
// per tier so the operator can see + edit every multiplier without
// having to paste JSON.
const TIERS = ['bronze', 'silver', 'gold', 'platinum', 'diamond'] as const;

interface FormState {
  pointsPerKwh: string;
  sessionCompletionBonus: string;
  referralBonusReferrer: string;
  referralBonusReferee: string;
  minRedeemablePoints: string;
  pointsPerDollar: string;
  tierMultipliers: Record<string, string>;
  // Friends Stage 2 — referral tier milestones. Strings during
  // editing (mirrors every other numeric field on this form) so
  // empty inputs aren't coerced to 0 / NaN mid-edit. Coerced to
  // numbers in formToPatch on save.
  referralTiers: Array<{ sessionCount: string; points: string }>;
  referralCap: string;            // empty when unlimited or unset
  referralCapUnlimited: boolean;  // UI-only checkbox state
}

function settingsToForm(s: RewardPointsSettings): FormState {
  const tier: Record<string, string> = {};
  for (const t of TIERS) tier[t] = String(s.tierMultipliers[t] ?? 1);
  return {
    pointsPerKwh: String(s.pointsPerKwh),
    sessionCompletionBonus: String(s.sessionCompletionBonus),
    referralBonusReferrer: String(s.referralBonusReferrer),
    referralBonusReferee: String(s.referralBonusReferee),
    minRedeemablePoints: String(s.minRedeemablePoints),
    pointsPerDollar: String(s.pointsPerDollar),
    tierMultipliers: tier,
    referralTiers: (s.referralTiers ?? []).map((t) => ({
      sessionCount: String(t.sessionCount),
      points: String(t.points),
    })),
    referralCap:
      s.referralMaxMilestonesPerFriend == null
        ? ''
        : String(s.referralMaxMilestonesPerFriend),
    referralCapUnlimited: s.referralMaxMilestonesPerFriend == null,
  };
}

function formToPatch(f: FormState): Partial<RewardPointsSettings> {
  const num = (s: string): number => Number(s);
  const tier: Record<string, number> = {};
  for (const t of TIERS) {
    const v = num(f.tierMultipliers[t] ?? '1');
    if (Number.isFinite(v) && v > 0) tier[t] = v;
  }
  // Referral tiers — coerce strings to numbers; the server
  // validates + sorts. Drop rows whose either field doesn't
  // parse to a positive integer (mirrors the reader's tolerance
  // — UI shouldn't block save on a half-edited row, the server
  // surfaces validation errors that the operator can correct).
  const referralTiers: Array<{ sessionCount: number; points: number }> = [];
  for (const row of f.referralTiers) {
    const sc = num(row.sessionCount);
    const pts = num(row.points);
    if (
      Number.isInteger(sc) && sc > 0
      && Number.isInteger(pts) && pts > 0
    ) {
      referralTiers.push({ sessionCount: sc, points: pts });
    }
  }
  let referralMaxMilestonesPerFriend: number | null;
  if (f.referralCapUnlimited) {
    referralMaxMilestonesPerFriend = null;
  } else {
    const cap = num(f.referralCap);
    referralMaxMilestonesPerFriend =
      Number.isInteger(cap) && cap > 0 ? cap : null;
  }
  return {
    pointsPerKwh: num(f.pointsPerKwh),
    sessionCompletionBonus: num(f.sessionCompletionBonus),
    referralBonusReferrer: num(f.referralBonusReferrer),
    referralBonusReferee: num(f.referralBonusReferee),
    minRedeemablePoints: num(f.minRedeemablePoints),
    pointsPerDollar: num(f.pointsPerDollar),
    tierMultipliers: tier,
    referralTiers,
    referralMaxMilestonesPerFriend,
  };
}

export function EarningRulesPage() {
  useRecentRoute('Earning Rules');
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchRewardPointsSettings()
      .then((res) => setForm(settingsToForm(res.value)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const setField = (key: keyof FormState, value: string): void => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };
  const setTierField = (tier: string, value: string): void => {
    setForm((f) => f ? {
      ...f,
      tierMultipliers: { ...f.tierMultipliers, [tier]: value },
    } : f);
  };

  // ── Referral tier helpers ───────────────────────────────────
  const setReferralTierField = (
    index: number,
    field: 'sessionCount' | 'points',
    value: string,
  ): void => {
    setForm((f) => {
      if (!f) return f;
      const next = f.referralTiers.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      );
      return { ...f, referralTiers: next };
    });
  };
  const addReferralTier = (): void => {
    setForm((f) => f ? {
      ...f,
      referralTiers: [...f.referralTiers, { sessionCount: '', points: '' }],
    } : f);
  };
  const removeReferralTier = (index: number): void => {
    setForm((f) => {
      if (!f) return f;
      // Defense-in-depth: also enforced by the disabled state on
      // the × button when only one tier remains. The UI state lets
      // operators turn the feature off entirely by removing every
      // tier — saving with zero tiers is a valid "disabled" config.
      if (f.referralTiers.length <= 1) return f;
      return {
        ...f,
        referralTiers: f.referralTiers.filter((_, i) => i !== index),
      };
    });
  };
  const setReferralCap = (value: string): void => {
    setForm((f) => f ? { ...f, referralCap: value } : f);
  };
  const setReferralCapUnlimited = (checked: boolean): void => {
    setForm((f) => f ? {
      ...f,
      referralCapUnlimited: checked,
      // When toggling to unlimited, clear the cap input so the
      // numeric field looks "off" rather than retaining a stale
      // value behind the disabled state. When toggling back off,
      // the operator picks a fresh value.
      referralCap: checked ? '' : (f.referralCap || '3'),
    } : f);
  };

  const handleSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError('');
    try {
      const patch = formToPatch(form);
      const next = await updateRewardPointsSettings(patch);
      setForm(settingsToForm(next));
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleInvalidate = async (): Promise<void> => {
    try {
      await invalidateRewardPointsCache();
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invalidate cache');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><Coins size={18} /></div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Earning Rules</h1>
          <p className={styles.subtitle}>Gamification rates, multipliers, and redemption thresholds.</p>
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading…</p>}
      {error && <div className={styles.alertErr}>{error}</div>}

      {form && (
        <form className={styles.card} onSubmit={handleSave}>
          <div className={styles.cardTitleRow}>
            <h2 className={styles.cardTitle}>Reward Points</h2>
            <span className={styles.cacheNote}>Server cache TTL: 60s · changes apply on next earn</span>
          </div>
          <p className={styles.cardLead}>
            Drives every earning pathway in the points engine. All values here are read at runtime —
            nothing is hardcoded. A save invalidates this tenant&apos;s cache instantly.
          </p>

          {savedAt && (
            <div className={styles.alertOk}>Saved · cache invalidated. New rates take effect on the next earn event.</div>
          )}

          {/* ── Earning rates ────────────────────────────────────── */}
          <div className={styles.grid}>
            <NumericField
              label="Points per kWh"
              hint="Base rate — multiplied by session kWh and tier multiplier."
              value={form.pointsPerKwh}
              suffix="pts/kWh"
              onChange={(v) => setField('pointsPerKwh', v)}
            />
            <NumericField
              label="Session completion bonus"
              hint="Flat bonus per completed session (also tier-multiplied)."
              value={form.sessionCompletionBonus}
              suffix="pts"
              onChange={(v) => setField('sessionCompletionBonus', v)}
            />
            <NumericField
              label="Referral bonus — referrer"
              hint="Awarded when the referee completes their first session."
              value={form.referralBonusReferrer}
              suffix="pts"
              onChange={(v) => setField('referralBonusReferrer', v)}
            />
            <NumericField
              label="Referral bonus — referee"
              hint="Welcome bonus for the new driver."
              value={form.referralBonusReferee}
              suffix="pts"
              onChange={(v) => setField('referralBonusReferee', v)}
            />
            {/* Daily streak base/cap controls retired in Phase 1 of
                the gamification rules update. Streak counter still
                ticks (charging-session days drive it now, not
                app-opens) and streak-dependent badges still trigger;
                the per-day points award is gone. Phase 1.b dropped
                the dailyStreakBaseBonus + dailyStreakMaxBonus
                settings from the schema entirely. */}
            <NumericField
              label="Min redeemable points"
              hint="Drivers can't redeem coupons below this balance."
              value={form.minRedeemablePoints}
              suffix="pts"
              onChange={(v) => setField('minRedeemablePoints', v)}
            />
            <NumericField
              label="Points per dollar"
              hint="Wallet conversion rate — used by the redemption flow."
              value={form.pointsPerDollar}
              suffix="pts/$"
              onChange={(v) => setField('pointsPerDollar', v)}
            />
          </div>

          {/* ── Tier multipliers ─────────────────────────────────── */}
          <div className={styles.tierBlock}>
            <p className={styles.tierTitle}>Tier multipliers</p>
            <p className={styles.tierLead}>
              Applied to every positive earn (kWh, session bonus, referral, daily streak).
              Badge rewards skip the multiplier — fixed by rarity.
            </p>
            <div className={styles.tierGrid}>
              {TIERS.map((tier) => (
                <div key={tier} className={styles.tierRow}>
                  <span className={styles.tierName}>{tier}</span>
                  <input
                    className={styles.tierInput}
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.tierMultipliers[tier] ?? '1'}
                    onChange={(e) => setTierField(tier, e.target.value)}
                  />
                  <span className={styles.tierMultSuffix}>×</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Referral rewards (Friends Stage 2) ──────────────────
              Tiered milestone awards to the referrer when the
              referred friend hits configured session-count
              thresholds. Sub-block inside the same card for now
              (matches the tier-multipliers pattern); a future
              expansion phase may split into a sibling card if
              more trigger types ship and the single-card layout
              gets crowded — see docs/tickets/friends-gamification-
              system.md for the broader vision. */}
          <div className={styles.referralBlock}>
            <p className={styles.referralTitle}>
              <Users size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Referral rewards
            </p>
            <p className={styles.tierLead}>
              When a referred friend hits each session-count
              milestone, the inviter earns the configured points.
              Tiers fire in order; the cap below limits how many
              milestones a single friend can trigger.
            </p>

            <div className={styles.referralTierList}>
              {form.referralTiers.length === 0 && (
                <p className={styles.referralEmptyNote}>
                  No tiers configured — referrers won&rsquo;t earn
                  referral milestone points until a tier is added.
                </p>
              )}
              {form.referralTiers.map((row, i) => (
                <div key={i} className={styles.referralTierRow}>
                  <div className={styles.referralTierField}>
                    <input
                      className={formStyles.input}
                      type="number"
                      min={1}
                      step={1}
                      value={row.sessionCount}
                      onChange={(e) => setReferralTierField(i, 'sessionCount', e.target.value)}
                      aria-label={`Tier ${i + 1} session count`}
                    />
                    <span className={styles.referralTierFieldSuffix}>session #</span>
                  </div>
                  <span className={styles.referralTierArrow} aria-hidden>→</span>
                  <div className={styles.referralTierField}>
                    <input
                      className={formStyles.input}
                      type="number"
                      min={1}
                      step={1}
                      value={row.points}
                      onChange={(e) => setReferralTierField(i, 'points', e.target.value)}
                      aria-label={`Tier ${i + 1} reward points`}
                    />
                    <span className={styles.referralTierFieldSuffix}>pts</span>
                  </div>
                  <button
                    type="button"
                    className={styles.referralTierRemoveBtn}
                    onClick={() => removeReferralTier(i)}
                    disabled={form.referralTiers.length <= 1}
                    aria-label={`Remove tier ${i + 1}`}
                    title={
                      form.referralTiers.length <= 1
                        ? 'At least one tier required (or remove all to disable referrals)'
                        : 'Remove this tier'
                    }
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className={styles.referralAddBtn}
              onClick={addReferralTier}
            >
              <Plus size={13} /> Add tier
            </button>

            <div className={styles.referralCapRow}>
              <label className={formStyles.label}>
                Max milestones per friend
              </label>
              <div className={styles.referralCapControls}>
                <input
                  className={formStyles.input}
                  type="number"
                  min={1}
                  step={1}
                  value={form.referralCap}
                  onChange={(e) => setReferralCap(e.target.value)}
                  disabled={form.referralCapUnlimited}
                  placeholder="3"
                  aria-label="Max milestones per friend"
                />
                <label className={styles.referralUnlimitedLabel}>
                  <input
                    type="checkbox"
                    checked={form.referralCapUnlimited}
                    onChange={(e) => setReferralCapUnlimited(e.target.checked)}
                  />
                  <span>Unlimited</span>
                </label>
              </div>
              <p className={formStyles.hint}>
                With 3 tiers and a cap of 3, every tier fires once per
                referred friend. Setting the cap below the tier count
                limits how many milestones each friend can trigger.
              </p>
            </div>
          </div>

          {/* ── Actions ──────────────────────────────────────────── */}
          <div className={styles.actions}>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={handleInvalidate}
              disabled={saving}
              title="Force-clear the server-side cache (no save)"
            >
              <RefreshCw size={13} /> Force refresh cache
            </button>
            <div className={styles.actionsRight}>
              <button
                type="submit"
                className={formStyles.btnPrimary}
                disabled={saving}
              >
                {saving
                  ? <><Loader2 size={13} /> Saving…</>
                  : <><Save size={13} /> Save changes</>}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

function NumericField({
  label, hint, value, suffix, onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  suffix?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className={formStyles.field}>
      <label className={formStyles.label}>{label}</label>
      <div className={styles.numericInputWrap}>
        <input
          className={formStyles.input}
          type="number"
          step="0.01"
          min={0}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix && <span className={styles.unitSuffix}>{suffix}</span>}
      </div>
      {hint && <p className={formStyles.hint}>{hint}</p>}
    </div>
  );
}
